import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import {
  WEB_COMPONENT_GRID_VIEW,
  WEB_COMPONENT_GZ_LINK,
  WEB_COMPONENT_GZ_TITLE,
} from '../../../webcomponents.constants';
import { GridViewWebComponent } from '../../../grid/gridView';
import { AccountDataGrid, AccountsGridViewData, ColumnIndexes, ColumnKeys, SecurityGroupRule } from '../types';
import { TemplateState } from '../../../types/common/types';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { GzTextWebComponent } from '../../../ui/gzText';
import { GzTitleWebComponent } from '../../../ui/gzTitle';
import { GzDialogWebComponent } from '../../../ui/gzDialog';
import { GzTooltipWebComponent } from '../../../ui/gzTooltip';
import { AccountsGridDataProvider } from '../dataProvider/AccountsGridDataProvider';
import {
  accessRuleRenderer,
  accountLockoutRenderer,
  companyRenderer,
  fullNameRenderer,
  passwordExpirationRenderer,
  roleRenderer,
  servicesRenderer,
  statusRenderer,
  twoFARenderer,
  userNameRenderer,
} from './customGridRenders';
import { PAGE_SIZE_OPTIONS } from '../../../companies/companiesGrid/view/gridConfig/companies-grid-page';
import { htmlEncode } from '../../../utils/escape.utils';
import { GzDialog } from '../../../ui/gzDialog/component/GzDialog';
import { AddAccountWebComponent } from '../../addAccount';
import { CreateAccountEvents } from '../../addAccount/types';
import { translateFn } from '../../../utils/translator.helper';
import { EditAccountWebComponent } from '../../editAccount';
import { EditAccountEvents, EditedAccount } from '../../editAccount/types';
import { showToast } from '../../helpers/showToast';
import { ToastSeverity } from '../../../ui/gzToastNotification/type';
import { AccountErrorCodes } from '../../common/types/AccountErrorCodes';
import { ConsoleRequest, DataProviderType } from '../../../utils/request';
import { AccountLoginSecurityEvents } from '../../common/accountLoginSecurity/types';
import { GzBackendError } from '../../common/types/GzBackendError';

export class AccountsGridView extends BaseWebComponentView {
  public dom: {
    $wrapper: HTMLElement;
    $gridContainer: HTMLElement;
    $gridView: GridViewWebComponent<ColumnIndexes, ColumnKeys>;
    $noResultsContainer: HTMLElement;
    $noResultsMessage: GzTitleWebComponent;
    $noResultsAdditionalText: GzTextWebComponent;
    $noResultsAddNewButton: GzButtonWebComponent;
    $noResultsAddNewTooltip: GzTooltipWebComponent;
    $addAccountButton: GzButtonWebComponent;
    $deleteAccountButton: GzButtonWebComponent;
    $title: HTMLElement;
  };

  getTranslations(): void {
    this.commonStrings = this.stringsTranslator.getCommonStrings();
  }

  setTranslatedStrings(): void {
    this.dom.$noResultsMessage.innerText = translateFn('Start adding accounts');
    this.dom.$noResultsAdditionalText.innerText = translateFn('There are no accounts here');
    this.dom.$noResultsAddNewButton.innerText = translateFn('Add New');
    this.dom.$addAccountButton.innerText = translateFn('Add account');
    this.dom.$deleteAccountButton.innerText = translateFn('Delete');
    this.dom.$title.textContent = translateFn('Accounts');
  }

  getDomElements(): void {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$gridContainer = this.shadow.querySelector('.grid');
    this.dom.$title = this.shadow.querySelector(WEB_COMPONENT_GZ_TITLE);
    this.dom.$gridView = this.shadow.querySelector(WEB_COMPONENT_GRID_VIEW) as GridViewWebComponent<
      ColumnIndexes,
      ColumnKeys
    >;
    this.dom.$noResultsContainer = this.shadow.querySelector('#no-results');
    this.dom.$noResultsMessage = this.shadow.querySelector('#no-results-message');
    this.dom.$noResultsAdditionalText = this.shadow.querySelector('#no-results-additional-text');
    this.dom.$noResultsAddNewButton = this.shadow.querySelector('#no-results-add-new-button');
    this.dom.$noResultsAddNewTooltip = this.shadow.querySelector('#no-results-add-new-tooltip');
    this.dom.$addAccountButton = this.shadow.querySelector('#add-account-button');
    this.dom.$deleteAccountButton = this.shadow.querySelector('#delete-account-button');
    if (this.dom.$gridView.injectDependencies) {
      this.dom.$gridView.injectDependencies(this.dependencies);
      this.dom.$gridView.start();
    }

    this.getTranslations();
    this.setTranslatedStrings();
  }

  buildSuccessView(data: AccountsGridViewData): void {
    this.resetTemplateState(this.dom.$wrapper);

    this.buildGridView(data);
  }

  private buildGridView(data: AccountsGridViewData): void {
    const view = data.views[0];

    this.dom.$gridView.dataProvider = new AccountsGridDataProvider(this.dependencies, data.allColumns);

    this.dom.$gridView.sideMenuParent = this.shadow;

    const dataProvider = this.dom.$gridView.dataProvider as AccountsGridDataProvider;
    const gridState = dataProvider.getViewState();

    this.dom.$gridView.columnRenderers = {
      [ColumnKeys.FULL_NAME]: fullNameRenderer.bind(this),
      [ColumnKeys.USERNAME]: userNameRenderer.bind(this),
      [ColumnKeys.STATUS]: statusRenderer.bind(this),
      [ColumnKeys.ROLE]: roleRenderer.bind(this),
      [ColumnKeys.SERVICES]: servicesRenderer.bind(this),
      [ColumnKeys.COMPANY]: companyRenderer.bind(this),
      [ColumnKeys.TWO_FA]: twoFARenderer.bind(this),
      [ColumnKeys.PASSWORD_EXPIRATION]: passwordExpirationRenderer.bind(this),
      [ColumnKeys.ACCOUNT_LOCKOUT]: accountLockoutRenderer.bind(this),
      [ColumnKeys.ACCESS_RULE]: accessRuleRenderer.bind(this),
    };
    this.dom.$gridView.viewId = view.id;
    this.dom.$gridView.allColumnDetails = data.allColumns;
    this.dom.$gridView.columns = gridState.columns ?? view.displayedColumns;
    this.dom.$gridView.filters = gridState.filters ?? view.filters;
    this.dom.$gridView.predefinedFiltersKeys = data.predefinedFilterKeys;
    this.dom.$gridView.isCompactViewChecked = gridState.compactView ?? false;
    this.dom.$gridView.defaultViewProps = data.defaultViewProps;
    this.dom.$gridView.hasPagination = true;
    this.dom.$gridView.pageSizeOptions = PAGE_SIZE_OPTIONS;
    this.dom.$gridView.pageSize = gridState.pageSize ?? view.pageSize;
    this.dom.$gridView.hasSelectableRows = true;
    this.dom.$gridView.customStrings = {
      noSearchResultsString: translateFn('No accounts found'),
    };
    this.dom.$gridView.showGlobalActions = true;
    this.dom.$gridView.propertiesSet = true;
  }

  buildErrorView(error: Error): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  public openAddAccountWindow(): void {
    const addAccountComponent = new AddAccountWebComponent();

    const windowTitle = htmlEncode(translateFn('Add account'));
    const windowConfigs: GzDialog = {
      headline: windowTitle,
      bodyContent: addAccountComponent,
      isOpen: true,
      closableOutsideBox: false,
      isWindow: true,
    };

    const dialog = GzDialogWebComponent.buildDialogElement(
      windowConfigs,
      document.querySelector('.mainContainer') as HTMLElement
    );

    addAccountComponent.addEventListener(CreateAccountEvents.Cancel, () => {
      dialog.close();
    });
    addAccountComponent.addEventListener(CreateAccountEvents.AccountCreated, () => {
      dialog.close();
      this.refreshGrid();
    });

    addAccountComponent.injectDependencies(this.dependencies);
    addAccountComponent.start().then().catch();
  }

  private openEditAccountWindow(accountData: AccountDataGrid): void {
    const editAccountComponent = new EditAccountWebComponent();

    const windowTitle = htmlEncode(translateFn('Edit account'));
    const windowConfigs: GzDialog = {
      headline: windowTitle,
      bodyContent: editAccountComponent,
      isOpen: true,
      closableOutsideBox: false,
      isWindow: true,
    };

    const dialog = GzDialogWebComponent.buildDialogElement(
      windowConfigs,
      document.querySelector('.mainContainer') as HTMLElement
    );
    editAccountComponent.addEventListener(CreateAccountEvents.Cancel, () => {
      dialog.close();
    });
    editAccountComponent.addEventListener(EditAccountEvents.AccountUpdated, () => {
      dialog.close();
      this.refreshGrid();
    });
    editAccountComponent.addEventListener(AccountLoginSecurityEvents.TWO_FA_RESET, () => {
      this.refreshGrid();
    });

    editAccountComponent.injectDependencies(this.dependencies);
    editAccountComponent.setViewData(accountData as EditedAccount);
    editAccountComponent.start().then().catch();
  }

  public showDeleteAccountConfirmationWindow(selectedRecords: AccountDataGrid[]): void {
    const windowBodyHtml = `
      <p>
        ${translateFn('Once deleted, accounts cannot be restored. You will lose access to all associated data.')}
      </p>`;

    const windowFooterHtml = `<gz-button id='confirm' type='outline' color='negative' class='mr-2'>
       ${translateFn('Delete')}
      </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${translateFn('Cancel')}
      </gz-button>`;

    const windowTitle = selectedRecords.length === 1 ? translateFn('Delete account?') : translateFn('Delete accounts?');

    const dialog = GzDialogWebComponent.computeCustomDialog(
      windowBodyHtml,
      windowFooterHtml,
      windowTitle,
      null,
      async () => {
        const confirmBtn = dialog.shadowRoot?.querySelector<GzButtonWebComponent>('#confirm');

        if (confirmBtn) {
          confirmBtn.showSpinner = true;
          confirmBtn.disabled = true;
        }

        await this.removeRecords(selectedRecords);
        dialog.close();
      },
      null,
      false
    );
  }

  public showDeleteAccountMdrWarningWindow(deletedUserIds: string[], affectedDescendantsCount: number): void {
    const companyText = affectedDescendantsCount > 1 ? translateFn('companies') : translateFn('company');
    const windowBodyHtml = `<gz-dialog-notification-content
        iconName="ds-icon-notification_negative_XL"
        iconColor="var(--red-layout-300-primary)"
      >
        <div slot="message">
           <gz-text fontSize="14" style="margin-bottom: 16px">
            ${translateFn(
              'Your selection includes one or more MDR emergency contacts. Provide new MDR contacts for the {affectedDescendantsCount} affected {companyOrCompanies} and then try again.',
              {
                affectedDescendantsCount: '<b>' + affectedDescendantsCount + '</b>',
                companyOrCompanies: companyText,
              }
            )}</gz-text>
            <gz-text fontSize="14">${translateFn(
              'Download the CSV file for a full list of affected companies.'
            )}</gz-text>
        </div>
      </gz-dialog-notification-content>`;

    const windowFooterHtml = `
      <gz-button id='confirm' type='full' color='primary' class='mr-2'>
         ${translateFn('Download CSV')}
      </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${translateFn('Cancel')}
      </gz-button>`;

    const windowTitle = translateFn('Cannot delete account');

    GzDialogWebComponent.computeCustomDialog(windowBodyHtml, windowFooterHtml, windowTitle, null, () => {
      this.dependencies.api.clients.Users.downloadCSVFileWithAffectedMdrCompanies(deletedUserIds)
        .toPromise()
        .then()
        .catch();
    });
  }

  private async removeRecords(selectedRecords: AccountDataGrid[]) {
    try {
      await ConsoleRequest.createRequest<any>({
        dataProviderType: DataProviderType.CORE,
        endpoint: 'Users',
        method: 'removeRecords',
        payload: {
          records: selectedRecords,
        },
      });

      showToast(
        ToastSeverity.SUCCESS,
        selectedRecords.length === 1 ? translateFn('Account deleted') : translateFn('Accounts deleted')
      );
      this.refreshGrid();
    } catch (error) {
      let message;
      switch (error?.err?.code) {
        case AccountErrorCodes.USER_DOES_NOT_EXIST:
          message = translateFn('Users no longer exist. Please refresh the grid.');
          break;
        case AccountErrorCodes.AD_USERS_CANNOT_DELETE_PLATFORM_MANAGERS_ACCOUNTS:
        case AccountErrorCodes.USER_DOES_NOT_EXIST_OR_DO_NOT_HAVE_PERMISSION:
          message = translateFn('You do not have permission to delete this account.');
          break;
        default:
          message = translateFn('An error has occurred. Please try again later.');
          break;
      }

      showToast(ToastSeverity.ERROR, message);
    }
  }

  public updateDeleteBtnAvailability(selectedRecords: string[]): void {
    if (!selectedRecords) {
      this.dom.$deleteAccountButton.disabled = true;
      return;
    }

    // no selected items -> delete button should be disabled
    if (selectedRecords.length === 0) {
      this.dom.$deleteAccountButton.disabled = true;
      return;
    }

    // cannot remove users with readOnly attribute
    for (let i = 0; i < selectedRecords.length; i++) {
      if (selectedRecords[i].hasOwnProperty('readOnly') && selectedRecords[i]['readOnly'] === true) {
        this.dom.$deleteAccountButton.disabled = true;
        return;
      }
    }

    this.dom.$deleteAccountButton.disabled = false;
  }

  private refreshGrid(): void {
    if (!this.dom.$gridView.controller) {
      return;
    }
    this.dom.$gridView.controller.handleGridItemsLoad(true, true);
  }

  private onClickOnRuleName(ruleData: SecurityGroupRule): void {
    this.dom.$gridView.dispatchEvent(
      new CustomEvent('editRule', {
        detail: {
          ruleData,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  private onFullNameClick(accountData: AccountDataGrid): void {
    this.openEditAccountWindow(accountData);
  }

  private onUsernameClick(accountData: AccountDataGrid): void {
    this.openEditAccountWindow(accountData);
  }

  public async setGridErrorMessage(error: GzBackendError | undefined) {
    let dataLoadingFailedMessage = htmlEncode(
      translateFn(
        'Data loading has failed. Please try to refresh the page and if the issue persists contact Bitdefender Enterprise Support for more information.'
      )
    );

    if (error?.err?.code === AccountErrorCodes.COMPANY_DOES_NOT_EXIST_OR_IS_NOT_ACCESSIBLE) {
      dataLoadingFailedMessage = translateFn(
        'You have companies in your saved filters that does not exist or are not accessible anymore. Please change your company filter.'
      );
    }

    this.dom.$gridView.customStrings.dataLoadingFailed = dataLoadingFailedMessage;
    this.dom.$gridView.controller?.updateCustomStrings();
  }
}
